import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/auth';
import { GenericResponse, InstallationSavedData, RazonSocial } from 'src/app/interfaces';
import { NewContractPymesService } from 'src/app/services/new-contract-pymes.service';
import { UtilService } from 'src/app/services/util.service';
import { ToastrService } from 'src/app/shared/ui/toastr/toastr.service';
import { AttachmentsDraft, FileDraft } from '../new-contract-pymes.model';
import { DigitalDialogComponent } from './digital-dialog/digital-dialog.component';
import { PaperDialogComponent } from './paper-dialog/paper-dialog.component';
import { PaperSentDialogComponent } from './paper-sent-dialog/paper-sent-dialog.component';
import { SentDialogComponent } from './sent-dialog/sent-dialog.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-contract-signing',
  templateUrl: './contract-signing.component.html',
  styleUrls: ['./contract-signing.component.scss']
})
export class ContractSigningComponent implements OnInit, OnDestroy {
  @Output() formValid = new EventEmitter<boolean>();
  @Output() isLoading: EventEmitter<boolean> = new EventEmitter();
  @Input() multipoint = false;
  @Input() hasContractFile = true;
  @Input() hasSepaFile = true;
  @Input() hasChangedBankAccount = true;
  @Input() hasChangedDni = true;
  @Input() finishCreateFiles = false;
  @ViewChild('inputFile') input: ElementRef<HTMLInputElement>;
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  private savedDataBusiness: RazonSocial = null;
  private savedDataInstallation: InstallationSavedData = null;
  public isPaperType = true;
  public uploadFiles: boolean;
  public uploadFilesDigital: boolean;
  private fileType: string;
  public disabledAddSepa = true;
  public bankAccountsSepa = [];
  public isBankAccounts = false;
  private account: string;
  public digitalFileList: FileDraft[] = [];
  public paperFilesList: FileDraft[] = [];
  private codeContract: string;
  public disabledButtons = false;
  public isUserSpanish = true;
  public allowAsyncContract: boolean;
  private isNewContract: boolean;
  private sepaRequired: boolean;

  constructor(
    private paperDialog: MatDialog,
    private paperSentDialog: MatDialog,
    private digitalDialog: MatDialog,
    private downloadDialog: MatDialog,
    private newContractService: NewContractPymesService,
    private toastrService: ToastrService,
    private utilService: UtilService,
    private authService: AuthService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.isUserSpanish = this.authService.getUserData().country === 'ES';
    this.allowAsyncContract = this.multipoint ? false : this.authService.getUserData().allowContratacionAsyncrona;
    this.checkContractType();
    this.getSavedData();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private getSavedData(): void {
    this.newContractService.getSavedData().pipe(takeUntil(this.destroyed$)).subscribe(res => {
      if (res) {
        this.savedDataBusiness = res.business;
        this.savedDataInstallation = res.installation;
        this.disabledAddSepa = this.checkBooleanString(res?.disabledAddSepa);
        this.bankAccountsSepa = res.bankAccountsSepa;
        this.isBankAccounts = !!res?.bankAccountsSepa;
        this.codeContract = res?.codigoContrato;
        if (res?.needFiles) {
          this.getAttachmentsDraft(true);
        }
        if (!this.isUserSpanish) {
          this.checkUrldisabledAddSepa(res?.analysis?.payment?.isMultibanco ?? res?.payment?.isMultibanco);
        }
        this.hasChangedBankAccount = this.allowAsyncContract
          ? (this.hasChangedBankAccount || this.checkBooleanString(res?.hasChangedBankAccount))
          : this.checkBooleanString(res?.hasChangedBankAccount);
        this.hasChangedDni = this.allowAsyncContract
          ? (this.hasChangedDni || this.checkBooleanString(res?.hasChangedDni))
          : this.checkBooleanString(res?.hasChangedDni);
        this.disabledButtons = !!res.disabledButtons;
      }
    });
  }

  private checkUrldisabledAddSepa(isMultibanco: boolean): void {
    const disabledAddSepaFromUrl = this.router.url.includes('disabledAddSepa=')
      ? this.router.url.split('disabledAddSepa=').pop().split('&')[0] : 'false';
    this.disabledAddSepa = typeof(isMultibanco) === 'boolean' ? isMultibanco : disabledAddSepaFromUrl === 'true';
    const sepaRequiredFromUrl = this.router.url.includes('sepaRequired=')
      ? this.router.url.split('sepaRequired=').pop().split('&')[0] : 'false';
    this.sepaRequired = typeof(isMultibanco) === 'boolean' ? !isMultibanco : sepaRequiredFromUrl === 'true';
  }

  private checkBooleanString(value: string): boolean {
    if (typeof (value) === 'boolean') {
      return value;
    }
    return value === null || value === undefined ? true : value === 'true';
  }

  public toggleType(option: boolean): void {
    this.isPaperType = option;
    this.uploadFiles = this.checkUploadFiles();
    this.uploadFilesDigital = this.checkUploadFilesDigital();
  }

  public download(): void {
    this.isLoading.emit(true);
    this.toastrService.success('NEW_CONTRACTS.DOWNLOADING_FOR_SIGNATURE');
    this.newContractService.getContractFile(this.codeContract).subscribe(
      result => {
        if (result.body.size) {
          const header = result.headers.get('Content-Disposition');
          const fileName = header.split(';')[1].split('=')[1].replace(/\"/g, '');
          const link = document.createElement('a');
          link.download = fileName;
          link.href = URL.createObjectURL(result.body);
          link.click();
          this.isLoading.emit(false);
        } else {
          const reader = new FileReader();
          reader.addEventListener('loadend', e => {
            this.toastrService.error(JSON.parse(e.target.result as string).Message);
            this.isLoading.emit(false);
          });
          reader.readAsText(result.body);
        }
      },
      error => {
        this.isLoading.emit(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  public downloadSepa(): void {
    this.isLoading.emit(true);
    this.toastrService.success('NEW_CONTRACTS.DOWNLOADING_SEPA_FOR_SIGNATURE');
    const service = this.multipoint ?
      this.newContractService.getSepaMultipoint(this.codeContract) :
      this.newContractService.getPdfContract(this.codeContract, 'contrato.getSepa');
    service.subscribe(
      result => {
        if (result.type !== 'application/json') {
          this.toastrService.success('COMMONS.DOWNLOADING_SUCCESS');
          this.disabledAddSepa = false;
          if (this.multipoint) {
            const header = result.headers.get('Content-Disposition');
            const fileName = header.split(';')[1].split('=')[1].replace(/\"/g, '');
            const link = document.createElement('a');
            link.download = fileName;
            link.href = URL.createObjectURL(result.body);
            link.click();
          } else {
            window.open(URL.createObjectURL(result));
          }
        } else {
          const reader = new FileReader();
          reader.addEventListener('loadend', e => {
            this.toastrService.error(JSON.parse(e.target.result as string).Message);
            this.isLoading.emit(false);
          });
          reader.readAsText(result);
          this.disabledAddSepa = true;
        }
        this.isLoading.emit(false);
      },
      error => {
        this.isLoading.emit(false);
        this.utilService.toastErrors(error);
        this.disabledAddSepa = true;
      }
    );
  }

  public openFile(fileType?: string, account: string = null): void {
    this.fileType = fileType;
    this.account = account;
    this.input.nativeElement.click();
  }

  public selectFile(file: FileDraft): void {
    if (file[0]) {
      const condition = el => el.nombreFichero !== file[0].name
        || el.tipo !== this.fileType || el.iban !== this.account;
      const notExist = this.isPaperType ? this.paperFilesList?.every(condition) : this.digitalFileList?.every(condition);
      if (notExist) {
        // Max 10Mb
        if (file[0].size < 10000000) {
          this.sendFile(file[0]);
        } else {
          this.toastrService.error('COMMONS.ERROR_SIZE_FILES');
        }
      } else {
        this.toastrService.error('NEW_CONTRACTS.ERROR_DUPLICATE_FILE');
      }
    }
    this.input.nativeElement.value = '';
  }

  private sendFile(file: File): void {
    this.isLoading.emit(true);
    const formData = new FormData();
    formData.append('Documento', file);
    formData.append('CodigoContrato', this.codeContract);
    formData.append('TipoAdjunto', this.fileType);
    formData.append('isFirmaDigital', this.isPaperType ? 'false' : ' true');
    if (this.account) {
      formData.append('iban', this.account);
    }
    this.toastrService.success('NEW_CONTRACTS.FILE_SENDING');
    this.newContractService.postAttachmentsDraft(formData).pipe(takeUntil(this.destroyed$)).subscribe(res => {
      this.isLoading.emit(false);
      if (res.success) {
        this.getAttachmentsDraft();
      } else {
        this.utilService.toastErrors(res);
      }
    }, error => {
      this.isLoading.emit(false);
      this.utilService.toastErrors(error);
    });
  }

  private getAttachmentsDraft(isFisrtTime = false): void {
    this.isLoading.emit(true);
    this.newContractService.getAttachmentsDraft(this.codeContract).pipe(takeUntil(this.destroyed$)).subscribe(
      (res: GenericResponse<AttachmentsDraft>) => {
        if (res.success) {
          this.digitalFileList = res.data?.documentosDigital ?? [];
          this.paperFilesList = res.data?.documentosPapel ?? [];
          this.uploadFiles = this.checkUploadFiles();
          this.uploadFilesDigital = this.checkUploadFilesDigital();
          if (isFisrtTime && res.data?.isFirmaDigital !== undefined) {
            this.isPaperType = !res.data.isFirmaDigital;
          }
        } else {
          this.utilService.toastErrors(res);
        }
        this.isLoading.emit(false);
      }, error => {
        this.utilService.toastErrors(error);
        this.isLoading.emit(false);
      }
    );
  }

  private checkUploadFiles(): boolean {
    const paperCheckSepa = this.isUserSpanish ? this.paperFilesList?.some(el => el.tipo === 'MandatoSepa')
      : (this.sepaRequired ? this.paperFilesList?.some(el => el.tipo === 'MandatoSepa') : true);
    if (this.isUserSpanish || (!this.isUserSpanish && this.disabledButtons)) {
      return (this.isPaperType)
        ? ((this.isNewContract && !this.disabledAddSepa)
          ? (this.paperFilesList?.some(el => el.tipo === 'ContratoPyme') && paperCheckSepa)
          : this.paperFilesList?.some(el => el.tipo === 'ContratoPyme'))
        : this.digitalFileList?.some(el => el.tipo === 'ContratoPyme');
    } else {
      return (this.isPaperType)
        ? (this.paperFilesList?.some(el => el.tipo === 'ContratoPyme') &&
          paperCheckSepa &&
          this.paperFilesList?.some(el => el.tipo === 'Comprovativo') &&
          this.paperFilesList?.some(el => el.tipo === 'AutorizacionCanal') &&
          this.paperFilesList?.some(el => el.tipo === 'Dni') &&
          this.paperFilesList?.some(el => el.tipo === 'TarjetaIdentificacionFiscal'))
        : this.digitalFileList?.some(el => el.tipo === 'ContratoPyme');
    }
  }

  private checkUploadFilesDigital(): boolean {
      return this.digitalFileList?.some(el => el.tipo === 'Comprovativo') &&
          this.digitalFileList?.some(el => el.tipo === 'AutorizacionCanal') &&
          this.digitalFileList?.some(el => el.tipo === 'Dni') &&
          this.digitalFileList?.some(el => el.tipo === 'TarjetaIdentificacionFiscal');
  }

  public deleteFile(i: number): void {
    this.isPaperType
      ? this.deleteAttachmentsDraft(this.paperFilesList[i].id)
      : this.deleteAttachmentsDraft(this.digitalFileList[i].id);
  }

  private deleteAttachmentsDraft(idFileDraft: number): void {
    this.isLoading.emit(true);
    this.toastrService.success('NEW_CONTRACTS.FILE_DELETING');
    this.newContractService.deleteAttachmentsDraft(idFileDraft).pipe(takeUntil(this.destroyed$)).subscribe(
      (res: GenericResponse<AttachmentsDraft>) => {
        if (res.success) {
          this.getAttachmentsDraft();
        } else {
          this.utilService.toastErrors(res);
        }
        this.isLoading.emit(false);
      }, error => {
        this.utilService.toastErrors(error);
        this.isLoading.emit(false);
      }
    );
  }

  public openPaperDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      width: '575px'
    };
    const dialogRef = this.paperDialog.open(PaperDialogComponent, dialogConfig.data);
    dialogRef.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(result => {
      if (result) {
        this.attachFiles(this.paperFilesList[0], 0);
      }
    });
  }

  public openDigitalDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      width: '575px'
    };
    const dialogRef = this.digitalDialog.open(DigitalDialogComponent, dialogConfig.data);
    dialogRef.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(result => {
      if (result && this.digitalFileList.length > 0) {
        this.attachFiles(this.digitalFileList[0], 0);
      } else if (result && this.digitalFileList.length === 0) {
        this.sendDigitalContract();
      }
    });
  }

  private attachFiles(file: FileDraft, index: number): void {
    this.isLoading.emit(true);
    const formData = new FormData();
    const byteString = window.atob(file.fichero);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    }
    const document = new File([int8Array], file.nombreFichero);
    formData.append('Documento', document);
    formData.append('CodigoContrato', this.newContractService.getSavedData().value.codigoContrato);
    if (file.tipo === 'Comprovativo') {
      formData.append('TipoAdjunto', 'Otros');
    } else {
      formData.append('TipoAdjunto', file.tipo);
    }
    this.toastrService.success('NEW_CONTRACTS.FILE_SENDING');
    const data = this.isPaperType ? this.paperFilesList : this.digitalFileList;
    const apiCall = this.allowAsyncContract ?
      this.newContractService.setAttachedFileAsync(formData) :
      this.newContractService.setAttachedFile(formData);
    apiCall.pipe(takeUntil(this.destroyed$)).subscribe(res => {
        if (res.success) {
          data[index].sent = true;
          if (index < data.length - 1) {
            this.attachFiles(data[index + 1], index + 1);
          } else {
            this.isLoading.emit(false);
            this.uploadFiles = false;
            if (this.isPaperType) {
              this.sendPaperContract();
            } else {
              this.sendDigitalContract();
            }
          }
        } else {
          this.isLoading.emit(false);
          this.utilService.toastErrors(res);
        }
      }, error => {
        this.isLoading.emit(false);
        this.utilService.toastErrors(error);
      });
  }

  private sendPaperContract(): void {
    this.isLoading.emit(true);
    const params = {
      codContrato: this.codeContract,
      codRazonSocial: this.savedDataInstallation.codigoRazonSocial,
      nombreRazonSocial: this.savedDataBusiness.nombre
    };
    this.toastrService.success('NEW_CONTRACTS.CONTRACT_SENDING');
    const apiCall = this.allowAsyncContract ?
      this.newContractService.setFicheroContratoAsync(this.codeContract) :
      this.newContractService.setFicheroContrato(params);
    apiCall.pipe(takeUntil(this.destroyed$)).subscribe(
      res => {
        this.isLoading.emit(false);
        if (res.success) {
          this.uploadFiles = false;
          this.openPaperSentDialog();
        } else {
          this.utilService.toastErrors(res);
          this.uploadFiles = true;
        }
      },
      error => {
        this.handleAfterError(error);
      }
    );
  }

  private openPaperSentDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      width: '575px',
      disableClose: true,
    };
    this.paperSentDialog.open(PaperSentDialogComponent, dialogConfig.data);
  }

  private sendDigitalContract(): void {
    this.isLoading.emit(true);
    const apiCall = this.allowAsyncContract ?
      this.newContractService.firmaDigitalAsync(this.codeContract) :
      this.newContractService.firmaDigital(this.codeContract);
    apiCall.pipe(takeUntil(this.destroyed$)).subscribe(
      res => {
        this.isLoading.emit(false);
        if (res.success) {
          this.openSentDialog();
        } else {
          this.utilService.toastErrors(res);
          this.uploadFiles = true;
        }
      },
      error => {
        this.handleAfterError(error);
      }
    );
  }

  private openSentDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      width: '575px',
    };
    this.downloadDialog.open(SentDialogComponent, dialogConfig.data);
  }

  private handleAfterError(error: any): void {
    this.uploadFiles = true;
    this.isLoading.emit(false);
    this.utilService.toastErrors(error);
  }

  public downloadFile(event: FileDraft): void {
    const byteString = window.atob(event.fichero);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    }
    const link = document.createElement('a');
    const blob = new Blob([int8Array], { type: event.nombreFichero.split('.').pop() });
    link.download = event.nombreFichero;
    link.href = URL.createObjectURL(blob);
    link.click();
  }

  private checkContractType(): void {
    const currentUrl = this.router.url.split('?')[0];
    this.isNewContract = currentUrl === '/draft' || currentUrl === '/new-contract-pymes-multipoint';
  }
}
